import { useCallback, useEffect, useState } from "react";
import i18n, { loadLanguage, normalizeLang, STORAGE_KEY, type AppLang } from "./index";

/**
 * Wrapper kring i18next för Fennys språkväljare.
 *
 * - Returnerar nuvarande normaliserade språk ("sv" | "en")
 * - Setter skriver till localStorage så valet överlever sessioner.
 * - Synkar `<html lang>` för a11y/SEO.
 *
 * VIKTIGT: SSR + första klient-render kör alltid "sv". Efter hydration läses
 * preferensen från URL (`/en/*` eller `?lang=`), localStorage, eller
 * navigator.language. Då anropas changeLanguage som triggar re-render.
 */
export function useLanguage() {
  const [lang, setLangState] = useState<AppLang>(() =>
    normalizeLang(i18n.resolvedLanguage ?? i18n.language),
  );

  useEffect(() => {
    const handler = (next: string) => {
      const n = normalizeLang(next);
      setLangState(n);
      if (typeof document !== "undefined") {
        document.documentElement.lang = n;
      }
    };
    i18n.on("languageChanged", handler);

    // URL-baserad språkdetektering kör vi i LocaleProvider per route.
    // Här hanterar vi bara persisted preference för chrome-komponenter
    // som råkar mountas utanför en LocaleProvider.
    let timer: number | undefined;
    if (typeof window !== "undefined") {
      timer = window.setTimeout(() => {
        let preferred: AppLang = "sv";
        try {
          const path = window.location.pathname;
          if (path === "/en" || path.startsWith("/en/")) {
            preferred = "en";
          } else {
            const stored = window.localStorage.getItem(STORAGE_KEY);
            if (stored) preferred = normalizeLang(stored);
          }
        } catch {
          /* ignore */
        }
        const current = normalizeLang(i18n.resolvedLanguage ?? i18n.language);
        if (preferred !== current) {
          void loadLanguage(preferred).then(() => i18n.changeLanguage(preferred));
        }
        if (typeof document !== "undefined") document.documentElement.lang = preferred;
      }, 0);
    }
    return () => {
      if (timer !== undefined) window.clearTimeout(timer);
      i18n.off("languageChanged", handler);
    };
  }, []);

  const setLang = useCallback((next: AppLang) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
    void loadLanguage(next).then(() => i18n.changeLanguage(next));
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === "sv" ? "en" : "sv");
  }, [lang, setLang]);

  return { lang, setLang, toggle };
}
